import React from 'react';
import { Grid as MuiGrid } from '@mui/material';
import { styled } from '@mui/material/styles';
import spacing from '../spacing';

/**
 * Grid Component
 * @component
 * @description Responsive grid system based on CSS Grid and Flexbox
 * 
 * @example
 * // Simple grid
 * <Grid container spacing={3}>
 *   <Grid item xs={12} md={6}>
 *     <Card>Content 1</Card>
 *   </Grid>
 *   <Grid item xs={12} md={6}>
 *     <Card>Content 2</Card>
 *   </Grid>
 * </Grid>
 * 
 * @example
 * // Auto-fit responsive columns
 * <Grid container columns={3} spacing={2}>
 *   <GridItem><Card /></GridItem>
 *   <GridItem><Card /></GridItem>
 *   <GridItem><Card /></GridItem>
 * </Grid>
 */

const StyledGrid = styled(MuiGrid)(({ spacing: spacingProp }) => {
  const spacingValue = typeof spacingProp === 'number' 
    ? spacing[spacingProp] 
    : spacingProp;

  return {
    ...(spacingProp && {
      margin: `-${spacingValue / 2}px`,
      width: `calc(100% + ${spacingValue}px)`,
      '& > .MuiGrid-item': {
        padding: `${spacingValue / 2}px`,
      },
    }),
  };
});

const Grid = ({
  children,
  container = false,
  item = false,
  spacing: spacingProp,
  xs,
  sm,
  md,
  lg,
  xl,
  ...props
}) => {
  return (
    <StyledGrid
      container={container}
      item={item}
      spacing={spacingProp}
      xs={xs}
      sm={sm}
      md={md}
      lg={lg}
      xl={xl}
      {...props}
    >
      {children}
    </StyledGrid>
  );
};

// CSS Grid based component for more modern layouts
const StyledCSSGrid = styled('div')(({
  columns = 'auto',
  gap = 4,
  minColumnWidth = '250px',
}) => {
  const gapValue = typeof gap === 'number' ? spacing[gap] : gap;
  
  return {
    display: 'grid',
    gap: gapValue,
    
    ...(typeof columns === 'number' ? {
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
    } : columns === 'auto' ? {
      gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))`,
    } : {
      gridTemplateColumns: columns,
    }),
  };
});

/**
 * CSSGrid Component - Modern CSS Grid layout
 */
export const CSSGrid = ({
  children,
  columns = 'auto',
  gap = 4,
  minColumnWidth = '250px',
  ...props
}) => {
  return (
    <StyledCSSGrid
      columns={columns}
      gap={gap}
      minColumnWidth={minColumnWidth}
      {...props}
    >
      {children}
    </StyledCSSGrid>
  );
};

export default Grid;
